/**
 * "Nem jelent meg az utas?" — the office's four-step procedure, as a sheet
 * that will not send until the steps have actually been done.
 *
 * THIS IS A CLAIM, NOT A STATUS. A no-show closes the trip AND bills the
 * customer, and the partner who sold it contests it by asking what the
 * driver did and from when they counted. The sheet used to be one warning
 * paragraph and two buttons; it collected nothing, so there was nothing to
 * answer with.
 *
 * WHAT GATES THE BUTTON (portalFormat's `noShowBlockers` — the same rules
 * the API refuses on, so the sheet never promises what the write will
 * decline):
 *
 *   the 60 minutes   at an airport, counted from the flight's ACTUAL
 *                    LANDING. Shown as a live countdown, the same shape as
 *                    the free-waiting clock above it. Not the scheduled
 *                    arrival, not the booked pickup: those are an hour or
 *                    more out, and a claim that matured while the plane was
 *                    still in the air is a claim the partner wins.
 *   the checkpoints  steps 1 and 2, ticked by the driver.
 *   the contact      step 3, and NOT a tick: the box is checked by the app
 *                    having recorded an attempt (`passenger_contact_attempts`),
 *                    because the instruction says to use the app precisely
 *                    so the attempt is on file. A WHATSAPP COUNTS as much as
 *                    a call — what is asked for is a recorded attempt to
 *                    reach the passenger, not the telephone specifically.
 *   the note         "írj rövid megjegyzést" is an instruction.
 *   the office       where no landing clock can decide — a flight we could
 *                    not read, or a pickup that is not at an airport at all
 *                    ("Nem repülőtéri fuvar esetén előzetes egyeztetés
 *                    nélkül ne jelents no-show-t"). The office is reached on
 *                    WhatsApp, as everywhere else in this portal.
 *
 * WHAT ONLY ADVISES: the photos ("lehetőség szerint"), and the GPS. A phone
 * that gives no fix says so on the sheet and the report still goes — with
 * the office told there is no corroboration, rather than an empty field
 * they might read as one.
 *
 * THE PHOTOS ARE FOUR LABELLED SLOTS, not a pile. What a photo was meant to
 * prove is stored with it, because nobody opening this a year later can
 * guess. The name-sign slot fills ITSELF from the picture taken while the
 * driver was waiting (NameSignAsk), and closes entirely when the sign was
 * the app's own — we rendered it, so we know what it said and a photograph
 * of a phone would add nothing.
 */
import React, { useCallback, useRef, useState } from 'react';
import { formatTime } from '../../../Utils/timezone';
import { downscaleImage } from '../imageDownscale';
import {
  formatElapsed,
  lastContactAttempt,
  canCallCustomer,
  nameSignAnswer,
  NO_SHOW_CHECKPOINTS,
  NO_SHOW_COPY,
  NO_SHOW_PHOTO_CATEGORIES,
  noShowBasis,
  noShowBlockers,
  noShowLandedAt,
  noShowWaitRemaining,
  noShowWaitedMinutes,
} from '../portalFormat';
import BottomSheet from './BottomSheet';
import OfficeWhatsAppButton from './OfficeWhatsAppButton';
import { CameraIcon } from './Icons';
import useNow from '../useNow';

const NOTE_MAX = 500;

/** The clock for the 60 minutes: a countdown, or the sentence it becomes. */
function WaitBlock({ trip, now, copy }) {
  const basis = noShowBasis(trip);

  if (basis === 'not_airport') {
    return <p className="pp-sheet__text" data-testid="no-show-basis-not-airport">{copy.notAirport}</p>;
  }

  if (basis === 'unknown') {
    // Deliberately NOT a countdown from something else. The flight card's
    // own sentence, plus what it means for this sheet.
    return <p className="pp-sheet__alert" role="status" data-testid="no-show-basis-unknown">{copy.landingUnknown}</p>;
  }

  const landedAt = formatTime(noShowLandedAt(trip));
  const remaining = noShowWaitRemaining(trip, now);

  if (remaining === 0) {
    return (
      <p className="pp-sheet__text" data-testid="no-show-wait-done">
        {copy.waitOver(landedAt, noShowWaitedMinutes(trip, now))}
      </p>
    );
  }

  return (
    <div className="pp-free" data-testid="no-show-wait">
      <div className="pp-free__row">
        <span className="pp-free__label">{copy.waitLabel}</span>
        <span
          className="pp-free__clock"
          role="timer"
          aria-label={copy.waitLabel}
          data-testid="no-show-wait-clock"
        >
          {formatElapsed(remaining)}
        </span>
      </div>
      <p className="pp-free__note">{copy.countedFrom(landedAt)}</p>
    </div>
  );
}

export default function NoShowSheet({
  trip,
  busy = false,
  error = '',
  position,
  signPhoto = null,
  driverName = '',
  onConfirm,
  onCancel,
  copy = NO_SHOW_COPY,
}) {
  // Steps 1 and 2. Step 3 is not in here on purpose — it is the app's
  // record, not a box (see `called`).
  const [checkpoints, setCheckpoints] = useState([]);
  const [note, setNote] = useState('');
  const [officeAgreed, setOfficeAgreed] = useState(false);
  // One photo per slot, keyed by category. The name-sign slot starts filled
  // when a picture was taken while waiting.
  const [photos, setPhotos] = useState(() => (signPhoto ? { name_sign: signPhoto } : {}));
  // Which slot is being processed ('' when idle), so only that one shows it.
  const [processing, setProcessing] = useState('');
  const [photoError, setPhotoError] = useState('');
  const inFlight = useRef(false);

  // The countdown only needs to tick while one is running.
  const ticking = noShowBasis(trip) === 'actual_landing';
  const now = useNow(ticking);

  const blockers = noShowBlockers(trip, {
    checkpoints, note, officeAgreed, now,
  });
  const ready = blockers.length === 0;
  const contacted = lastContactAttempt(trip);
  // The app's own sign needs no photograph, so that slot is not offered.
  const appSign = nameSignAnswer(trip)?.kind === 'app';

  const toggle = useCallback((checkpoint) => {
    setCheckpoints((current) => (current.includes(checkpoint)
      ? current.filter((value) => value !== checkpoint)
      : [...current, checkpoint]));
  }, []);

  const onPhoto = useCallback(async (category, event) => {
    const file = event.target.files && event.target.files[0];
    // Reset so choosing the same file again (a retake that failed) still fires.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!file) return;

    setProcessing(category);
    setPhotoError('');
    try {
      const taken = await downscaleImage(file);
      setPhotos((current) => ({ ...current, [category]: taken }));
    } catch {
      setPhotoError(copy.photoUnreadable);
    } finally {
      setProcessing('');
    }
  }, [copy]);

  const submit = useCallback(() => {
    if (inFlight.current || processing || !ready) return;
    inFlight.current = true;
    // One flat list, each item carrying what it is meant to prove — rather
    // than four arrays that could disagree about how many photos there are.
    onConfirm({
      checkpoints,
      note,
      officeAgreed,
      photos: NO_SHOW_PHOTO_CATEGORIES
        .filter((category) => photos[category])
        .map((category) => ({ ...photos[category], category })),
    });
    inFlight.current = false;
  }, [checkpoints, note, officeAgreed, onConfirm, photos, processing, ready]);

  return (
    <BottomSheet title={copy.title} onClose={onCancel} closeDisabled={busy}>
      <div className="pp-form pp-noshow">
        <p className="pp-sheet__text">{copy.lead}</p>
        <p className="pp-sheet__text pp-sheet__text--strong">{copy.airportRule}</p>

        <WaitBlock trip={trip} now={now} copy={copy} />

        <div className="pp-field">
          <span className="pp-field__label">{copy.stepsTitle}</span>

          {NO_SHOW_CHECKPOINTS.filter((checkpoint) => checkpoint !== 'called_passenger').map((checkpoint) => (
            <label className="pp-check" key={checkpoint} htmlFor={`pp-noshow-${checkpoint}`}>
              <input
                id={`pp-noshow-${checkpoint}`}
                type="checkbox"
                checked={checkpoints.includes(checkpoint)}
                onChange={() => toggle(checkpoint)}
                disabled={busy}
              />
              <span className="pp-check__body">
                <span className="pp-check__label">{copy.steps[checkpoint].label}</span>
                <span className="pp-check__hint">{copy.steps[checkpoint].hint}</span>
              </span>
            </label>
          ))}

          {/*
            Step 3 is shown as a checkbox for symmetry with the other two,
            but it is READ-ONLY: only a call the app recorded ticks it. A
            driver can tick a box; they cannot tick a phone call.
          */}
          <label className="pp-check pp-check--derived" htmlFor="pp-noshow-called_passenger">
            <input
              id="pp-noshow-called_passenger"
              type="checkbox"
              checked={Boolean(contacted)}
              readOnly
              disabled
            />
            <span className="pp-check__body">
              <span className="pp-check__label">{copy.steps.called_passenger.label}</span>
              <span className="pp-check__hint">
                {/* eslint-disable-next-line no-nested-ternary */}
                {contacted
                  ? copy.contactRecorded(contacted.method, formatTime(contacted.at))
                  : (canCallCustomer(trip) ? copy.callMissing : copy.callNoNumber)}
              </span>
            </span>
          </label>
        </div>

        <div className="pp-field">
          <label className="pp-field__label" htmlFor="pp-noshow-note">{copy.noteLabel}</label>
          <textarea
            id="pp-noshow-note"
            className="pp-input pp-input--area"
            rows={3}
            maxLength={NOTE_MAX}
            placeholder={copy.notePlaceholder}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={busy}
          />
        </div>

        {/*
          The GPS, said out loud in all three states — never silently
          claimed, and never warned about before the phone has answered.
          `undefined` is "still asking", `null` is "it gave nothing".
        */}
        {position === undefined ? (
          <p className="pp-sheet__note" data-testid="no-show-position-locating">{copy.positionLocating}</p>
        ) : (
          <p
            className={position ? 'pp-sheet__note' : 'pp-sheet__alert'}
            role={position ? undefined : 'status'}
            data-testid={position ? 'no-show-position-ok' : 'no-show-position-missing'}
          >
            {position ? copy.positionOk : copy.positionMissing}
          </p>
        )}

        {/*
          FOUR LABELLED SLOTS, not a pile. Compact on purpose — a driver at
          1am must not meet a form: each is one line until it holds
          something, and an empty one is never an error.
        */}
        <div className="pp-field">
          <span className="pp-field__label">
            {copy.photoLabel}
            <span className="pp-field__optional">{copy.photoOptional}</span>
          </span>

          {NO_SHOW_PHOTO_CATEGORIES.map((category) => {
            const slot = copy.photoSlots[category];
            const taken = photos[category];
            // The app drew its own sign, so there is nothing to photograph.
            if (category === 'name_sign' && appSign) {
              return (
                <p className="pp-sheet__note" key={category} data-testid="no-show-app-sign">
                  {copy.appSignNoPhoto}
                </p>
              );
            }
            const working = processing === category;

            return (
              <div className="pp-photo pp-photo--slot" key={category} data-testid={`no-show-photo-${category}`}>
                {/*
                  Same mechanics as the expense receipt: the input stays in
                  the accessibility tree and a <label> opens the camera,
                  which is the one way that works on every mobile browser.
                */}
                <input
                  id={`pp-noshow-photo-${category}`}
                  className="visually-hidden"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(event) => onPhoto(category, event)}
                  disabled={busy || Boolean(processing)}
                />
                {taken ? (
                  <div className="pp-photo__taken">
                    <img
                      className="pp-photo__thumb"
                      src={`data:${taken.type};base64,${taken.data}`}
                      alt={slot.alt}
                    />
                    <div className="pp-photo__side">
                      <span className="pp-photo__slot-label">{slot.label}</span>
                      <span className="pp-photo__ok">
                        {category === 'name_sign' && taken === signPhoto
                          ? copy.signPhotoAttached
                          : copy.photoReady}
                      </span>
                      <label
                        htmlFor={`pp-noshow-photo-${category}`}
                        className={`pp-btn pp-btn--outline${busy || processing ? ' pp-btn--inert' : ''}`}
                      >
                        <CameraIcon size={18} />
                        {working ? copy.processingPhoto : copy.retake}
                      </label>
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor={`pp-noshow-photo-${category}`}
                    className={`pp-photo__slot${working ? ' pp-photo__slot--busy' : ''}`}
                  >
                    <CameraIcon size={20} />
                    <span className="pp-photo__slot-body">
                      <span className="pp-photo__slot-label">
                        {working ? copy.processingPhoto : slot.label}
                      </span>
                      <span className="pp-photo__slot-hint">{slot.hint}</span>
                    </span>
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {/*
          Only where no clock can decide. With a landing on file this is not
          offered at all — the office's agreement cannot buy back minutes
          that have not passed, and the API refuses it anyway.
        */}
        {blockers.includes('office') || officeAgreed ? (
          <div className="pp-field" data-testid="no-show-office">
            <label className="pp-check" htmlFor="pp-noshow-office">
              <input
                id="pp-noshow-office"
                type="checkbox"
                checked={officeAgreed}
                onChange={() => setOfficeAgreed((value) => !value)}
                disabled={busy}
              />
              <span className="pp-check__body">
                <span className="pp-check__label">{copy.officeLabel}</span>
              </span>
            </label>
            <OfficeWhatsAppButton message={copy.officeMessage(trip, driverName)} />
          </div>
        ) : null}

        {photoError ? <div className="pp-form__error" role="alert">{photoError}</div> : null}
        {error ? <div className="pp-form__error" role="alert">{error}</div> : null}

        {/*
          WHY THE BUTTON IS STILL GREY. Always shown while anything is
          outstanding, never only after a press — the button is disabled, so
          a driver who cannot see the reason has no way to discover it.
        */}
        {ready ? null : (
          <p className="pp-sheet__note" role="status" data-testid="no-show-blocked">
            {blockers.map((blocker) => copy.blocked[blocker]).filter(Boolean).join(' ')}
          </p>
        )}

        <div className="pp-actions">
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-btn--grow pp-btn--tall"
            onClick={onCancel}
            disabled={busy}
          >
            {copy.cancel}
          </button>
          <button
            type="button"
            className="pp-btn pp-btn--dark pp-btn--grow pp-btn--tall"
            onClick={submit}
            disabled={busy || processing || !ready}
            data-testid="no-show-submit"
          >
            {busy ? copy.sending : copy.submit}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
